type TLocale = "en" | "ru" | "kk" | "bn" | "id" | "pt" | "es" | "uz" | "ky" | "ua";

/**
 * Maps browser language to supported locales
 */
const languageToLocaleMap: Record<string, TLocale> = {
  // English variants
  en: "en",
  "en-US": "en",
  "en-GB": "en",
  "en-AU": "en",
  "en-CA": "en",

  // Russian
  ru: "ru",
  "ru-RU": "ru",

  // Kazakh
  kk: "kk",
  "kk-KZ": "kk",

  // Bengali
  bn: "bn",
  "bn-IN": "bn",
  "bn-BD": "bn",

  // Indonesian
  id: "id",
  "id-ID": "id",

  // Portuguese
  pt: "pt",
  "pt-BR": "pt",
  "pt-PT": "pt",

  // Spanish
  es: "es",
  "es-ES": "es",
  "es-MX": "es",

  // Uzbek
  uz: "uz",
  "uz-UZ": "uz",

  // Kyrgyz
  ky: "ky",
  "ky-KG": "ky",

  // Ukrainian
  uk: "ua",
  "uk-UA": "ua",
};

/**
 * Detects locale from browser language settings
 */
export function detectLocaleByNavigator(supportedLocales: readonly TLocale[]): TLocale {
  // Get browser languages
  const browserLanguages = navigator.languages || [navigator.language];

  // Try to find a match in browser languages
  for (const lang of browserLanguages) {
    // Try exact match first
    if (languageToLocaleMap[lang]) {
      const locale = languageToLocaleMap[lang];
      if (supportedLocales.includes(locale)) {
        return locale;
      }
    }

    // Try base language  (e.g., "en" from "en-US")
    const baseLanguage = lang.split("-")[0] ?? "";
    if (languageToLocaleMap[baseLanguage]) {
      const locale = languageToLocaleMap[baseLanguage];
      if (supportedLocales.includes(locale)) {
        return locale;
      }
    }
  }

  // Default to first supported locale or "en"
  return supportedLocales.includes("en" as TLocale) ? ("en" as TLocale) : (supportedLocales[0] ?? "en");
}

/**
 * Country code to locale mapping for user geo-location detection
 */
const countryCodeToLocaleMap: Record<string, TLocale> = {
  // Example mapping - expand as needed
  US: "en",
  GB: "en",
  AU: "en",
  RU: "ru",
  KZ: "kk",
  IN: "bn",
  BD: "bn",
  ID: "id",
  BR: "pt",
  PT: "pt",
  ES: "es",
  UZ: "uz",
  KG: "ky",
  UA: "ua",
};

/**
 * Get locale by country code
 */
export function getLocaleByCountryCode(countryCode: string): TLocale | null {
  return countryCodeToLocaleMap[countryCode.toUpperCase()] || null;
}

export const countryLocaleMap = countryCodeToLocaleMap;
